import React, { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';
import { format, parseISO } from 'date-fns';

const Orders = () => {
  const { data, t, getDateLocale, formatUnit } = useAppContext();
  const [selectedOrder, setSelectedOrder] = useState(null);

  const formatDateSafely = (dateVal) => {
    if (!dateVal) return 'No Date';
    try {
      const dateStr = String(dateVal).split('T')[0];
      return format(parseISO(dateStr), 'MMM dd, yyyy', { locale: getDateLocale() });
    } catch (e) {
      return String(dateVal);
    }
  };

  const sortedOrders = [...(data.orders || [])].sort((a, b) => 
    new Date(b.delivery_date) - new Date(a.delivery_date)
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('orders.title')}</h1>
          <p className="text-gray-500 mt-1">{t('orders.subtitle')}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100 text-xs uppercase text-gray-500 font-semibold tracking-wider">
              <th className="p-4">{t('orders.table.date')}</th>
              <th className="p-4">{t('orders.table.customer')}</th>
              <th className="p-4">{t('orders.table.status')}</th>
              <th className="p-4">{t('orders.table.total')}</th>
              <th className="p-4 text-right">{t('orders.table.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sortedOrders.map(o => {
              const customer = data.customers.find(c => String(c.id) === String(o.customer_id));
              const lines = (data.orderLines || []).filter(ol => String(ol.order_id) === String(o.id));
              const totalQty = lines.reduce((sum, l) => sum + Number(l.quantity), 0);
              return (
                <tr key={o.id} className="hover:bg-gray-50 transition-colors">
                  <td className="p-4 font-medium text-gray-900">{formatDateSafely(o.delivery_date)}</td>
                  <td className="p-4 text-sm text-gray-600">{customer?.name || 'Unknown'}</td>
                  <td className="p-4">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">{o.status}</span>
                  </td>
                  <td className="p-4 text-sm text-gray-600">{totalQty} {totalQty === 1 ? t('units.unit') : t('units.units')}</td>
                  <td className="p-4 text-right">
                    <button
                      onClick={() => setSelectedOrder({ order: o, customer, lines })}
                      className="text-amber-600 hover:text-amber-800 font-medium text-sm"
                    >
                      {t('actions.viewDetails')}
                    </button>
                  </td>
                </tr>
              );
            })}
            {sortedOrders.length === 0 && (
              <tr><td colSpan="5" className="p-8 text-center text-gray-500">{t('orders.noOrders')}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 shadow-xl animate-fade-in">
            <div className="flex justify-between items-start mb-6 border-b border-gray-100 pb-4">
              <div>
                <h2 className="text-xl font-bold">{t('orders.details.title')}</h2>
                <p className="text-gray-500 text-sm mt-1">
                  {t('orders.details.delivery')} {formatDateSafely(selectedOrder.order.delivery_date)}
                </p>
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <SafeIcon name="X" className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-500 font-medium">{t('orders.details.customer')}</p>
              <p className="text-lg font-medium">{selectedOrder.customer?.name}</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 max-h-64 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="pb-2">{t('orders.details.product')}</th>
                    <th className="pb-2 text-right">{t('orders.details.qty')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {selectedOrder.lines.map(l => {
                    const product = data.products.find(p => String(p.id) === String(l.product_id));
                    return (
                      <tr key={l.id}>
                        <td className="py-2">{product?.name || 'Unknown Product'}</td>
                        <td className="py-2 text-right font-medium">{l.quantity} {formatUnit(product?.unit)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {selectedOrder.order.notes && (
              <div className="mt-4 p-3 bg-amber-50 text-amber-900 rounded-lg text-sm border border-amber-100">
                <span className="font-bold">{t('orders.details.notes')}</span> {selectedOrder.order.notes}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;